import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { commonService } from "../../services/common.services";
import authService from "../../services/auth.service";
import { apiConstants } from "../../constants/api.constant";
import toast from 'react-hot-toast';

// ============================================================================
// ASYNC THUNKS FOR AUTHENTICATION
// ============================================================================

/**
 * Login user with enhanced authentication
 */
export const login = createAsyncThunk(
    'user/login', 
    async (data, { rejectWithValue }) => {
  try {
    console.log("Redux login called with data:", data);
    
            // Use the new authentication service
            const result = await authService.login(data.email, data.password);
            
            if (result.success) {
                console.log("Login successful:", result.data);
                return result;
            } else {
                console.error("Login failed:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Login error in Redux:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Login failed";
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Logout user
 */
export const logout = createAsyncThunk(
    'user/logout',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux logout called");
            
            // Use the authentication service to logout
            await authService.logout();
            
            // Success toast removed - handled by component if needed
            
            return { success: true, message: "Logged out successfully" };
        } catch (error) {
            console.error("Logout error:", error);
            
            // Even if logout API fails, clear local session
            authService.clearSession();
            
            return { success: true, message: "Logged out successfully" };
        }
    }
);

/**
 * Refresh authentication token
 */
export const refreshToken = createAsyncThunk(
    'user/refreshToken',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux refresh token called");
            
            const newToken = await authService.refreshToken();
            
            if (newToken) {
                console.log("Token refreshed successfully");
                
                return {
                    success: true,
                    message: "Token refreshed successfully",
                    token: newToken
                };
            } else {
                throw new Error("Failed to refresh token");
            }
        } catch (error) {
            console.error("Token refresh error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Token refresh failed";
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Validate current token
 */
export const validateToken = createAsyncThunk(
    'user/validateToken',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux validate token called");
            
            const isValid = await authService.validateToken();
            
            if (isValid) {
                console.log("Token is valid");
                
                return {
                    success: true,
                    message: "Token is valid",
                    isValid: true
                };
            } else {
                console.log("Token is invalid");
                
                return {
                    success: false,
                    message: "Token is invalid",
                    isValid: false
                };
            }
      } catch (error) {
            console.error("Token validation error:", error);
            
            return rejectWithValue({
                success: false,
                message: "Token validation failed",
                isValid: false,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Change user password
 */
export const changePassword = createAsyncThunk(
    'user/changePassword',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux change password called");
            
            const result = await authService.changePassword(
                data.currentPassword,
                data.newPassword,
                data.confirmPassword,
                data.userId
            );
            
            if (result.success) {
                toast.success(result.message, {
                    position: "top-right",
                    duration: 3000,
                });
                
                return result;
    } else {
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Change password error:", error);
            
            let errorMessage = "Password change failed";

            // Safely handle error message extraction
            if (error?.response?.data?.detail) {
                if (typeof error.response.data.detail === 'string') {
                    errorMessage = error.response.data.detail;
                } else if (Array.isArray(error.response.data.detail)) {
                    errorMessage = error.response.data.detail.map(err => {
                        if (typeof err === 'object') {
                            return err.msg || err.message || 'Validation error';
                        }
                        return String(err);
                    }).join(', ');
                }
            } else if (error?.message) {
                errorMessage = String(error.message);
            }

            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Request password reset
 */
export const forgotPassword = createAsyncThunk(
    'user/forgotPassword',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux forgot password called");
            
            const result = await authService.forgotPassword(data.email);
            
            if (result.success) {
                toast.success(result.message, {
                    position: "top-right",
                    duration: 3000,
                });
                
                return result;
            } else {
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                
                return rejectWithValue(result);
    }
  } catch (error) {
            console.error("Forgot password error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Password reset request failed";
            
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Reset password with token
 */
export const resetPassword = createAsyncThunk(
    'user/resetPassword',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux reset password called");
            
            const result = await authService.resetPassword(
                data.token,
                data.newPassword
            );
            
            if (result.success) {
                toast.success(result.message, {
                    position: "top-right",
                    duration: 3000,
                });
                
                return result;
            } else {
                toast.error(result.message, {
      position: "top-right",
                    duration: 4000,
                });
                
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Reset password error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Password reset failed";
            
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Get session information
 */
export const getSessionInfo = createAsyncThunk(
    'user/getSessionInfo',
    async (_, { rejectWithValue }) => {
        try {
            console.log("Redux get session info called");
            
            const sessionInfo = await authService.getSessionInfo();
            
            return {
                success: true,
                message: "Session info retrieved successfully",
                data: sessionInfo
            };
        } catch (error) {
            console.error("Get session info error:", error);
            
            return rejectWithValue({
                success: false,
                message: "Failed to get session info",
                error: error?.response?.data
            });
        }
    }
);

// ============================================================================
// OTP AUTHENTICATION ASYNC THUNKS
// ============================================================================

/**
 * Initiate OTP-based login
 */
export const initiateOTPLogin = createAsyncThunk(
    'user/initiateOTPLogin',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux initiate OTP login called with data:", data);
            
            const result = await authService.initiateOTPLogin(data.username, data.password);
            
            if (result.success) {
                console.log("OTP login initiated successfully:", result.data);
                return result;
            } else {
                console.error("OTP login initiation failed:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Initiate OTP login error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Failed to initiate OTP login";
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Verify OTP and complete login
 */
export const verifyOTP = createAsyncThunk(
    'user/verifyOTP',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux verify OTP called with session:", data.sessionId);
            
            const result = await authService.verifyOTP(data.sessionId, data.otpCode);
            
            if (result.success) {
                console.log("OTP verified successfully:", result.data);
                return result;
            } else {
                console.error("OTP verification failed:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Verify OTP error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "OTP verification failed";
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Resend OTP code
 */
export const resendOTP = createAsyncThunk(
    'user/resendOTP',
    async (data, { rejectWithValue }) => {
        try {
            console.log("Redux resend OTP called with session:", data.sessionId);
            
            const result = await authService.resendOTP(data.sessionId);
            
            if (result.success) {
                console.log("OTP resent successfully:", result.data);
                toast.success(result.message, {
                    position: "top-right",
                    duration: 3000,
                });
                return result;
            } else {
                console.error("OTP resend failed:", result.message);
                toast.error(result.message, {
                    position: "top-right",
                    duration: 4000,
                });
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Resend OTP error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Failed to resend OTP";
            
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Get OTP session status
 */
export const getOTPStatus = createAsyncThunk(
    'user/getOTPStatus',
    async (sessionId, { rejectWithValue }) => {
        try {
            console.log("Redux get OTP status called for session:", sessionId);
            
            const result = await authService.getOTPStatus(sessionId);
            
            if (result.success) {
                console.log("OTP status retrieved successfully:", result.data);
                return result;
            } else {
                console.error("Failed to get OTP status:", result.message);
                return rejectWithValue(result);
            }
        } catch (error) {
            console.error("Get OTP status error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Failed to get OTP status";
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

// ============================================================================
// LEGACY ASYNC THUNKS (For backward compatibility)
// ============================================================================

/**
 * SMS verification (legacy - may need to be implemented in FastAPI)
 */
export const smsVerification = createAsyncThunk(
    'user/smsVerification', 
    async (data, { rejectWithValue }) => {
        try {
            const response = await commonService.withOutToken(apiConstants.SMS_VERIFICATION, data);
            
            if (response && response.data && response.data.success) {
    toast.success(response.data.msg, {
      position: "top-right",
                    duration: 2000,
                });
 
  return response.data; 
            } else {
                const errorMessage = response.data?.msg || "SMS verification failed";
                
                toast.error(errorMessage, {
                    position: "top-right",
                    duration: 4000,
                });
                
                return rejectWithValue({
                    success: false,
                    message: errorMessage
                });
            }
        } catch (error) {
            console.error("SMS verification error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "SMS verification failed";
            
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

/**
 * Get Staff Details (legacy)
 */
export const getStaffDetails = createAsyncThunk(
    'user/getStaffDetails', 
    async (data, { rejectWithValue }) => {
        try {
            const response = await commonService.withToken(apiConstants.GET_STAFF_DETAILS, data);
            
            if (response && response.data && response.data.success) {
  return response.data; 
            } else {
                const errorMessage = response.data?.msg || "Failed to get staff details";
                
                toast.error(errorMessage, {
                    position: "top-right",
                    duration: 4000,
                });
                
                return rejectWithValue({
                    success: false,
                    message: errorMessage
                });
            }
        } catch (error) {
            console.error("Get staff details error:", error);
            
            const errorMessage = error?.response?.data?.detail || 
                               error?.message || 
                               "Failed to get staff details";
            
            toast.error(errorMessage, {
                position: "top-right",
                duration: 4000,
            });
            
            return rejectWithValue({
                success: false,
                message: errorMessage,
                error: error?.response?.data
            });
        }
    }
);

// ============================================================================
// REDUX SLICE DEFINITION
// ============================================================================

const userSlice = createSlice({
  name: 'user',
  initialState: {
        // Authentication state
    loading: false,
    error: null,
        isAuthenticated: false,
    loggedInUser: null,
        authToken: null,
        refreshToken: null,
        
        // Session state
        sessionInfo: null,
        tokenValid: null,
        
        // RBAC state
        userRoles: [],
        userPermissions: [],
        effectivePermissions: {},
        hierarchyLevel: 0,
        canManageUsers: false,
        allowedDepartments: [],
        
        // Legacy state (for backward compatibility)
    smsVerificationData: null,
        getStaffDetailsData: null,
        
        // UI state
        loginAttempts: 0,
        lastLoginAttempt: null,
        passwordChangeRequired: false,
        
        // OTP authentication state
        otpSession: {
            sessionId: null,
            credentialsVerified: false,
            otpSent: false,
            otpVerified: false,
            emailMasked: null,
            expiresIn: null,
            canResendIn: null,
            attemptsRemaining: null,
            maxAttempts: null
        },
        otpLoading: false,
        otpError: null,
        
        // Feature flags
        features: {
            smsVerificationEnabled: false,
            passwordResetEnabled: true,
            sessionTimeoutEnabled: true,
            otpEnabled: true
        }
  },
  reducers: {
        // Synchronous actions
    changeValue: (state, action) => {
      Object.assign(state, action.payload);
    },
        
        // Clear authentication state
        clearAuth: (state) => {
            state.isAuthenticated = false;
            state.loggedInUser = null;
            state.authToken = null;
            state.refreshToken = null;
            state.sessionInfo = null;
            state.tokenValid = null;
            state.error = null;
            state.loading = false;
            // Clear RBAC state
            state.userRoles = [];
            state.userPermissions = [];
            state.effectivePermissions = {};
            state.hierarchyLevel = 0;
            state.canManageUsers = false;
            state.allowedDepartments = [];
            // Clear OTP state
            state.otpSession = {
                sessionId: null,
                credentialsVerified: false,
                otpSent: false,
                otpVerified: false,
                emailMasked: null,
                expiresIn: null,
                canResendIn: null,
                attemptsRemaining: null,
                maxAttempts: null
            };
            state.otpLoading = false;
            state.otpError = null;
        },
        
        // Set authentication state
        setAuth: (state, action) => {
            const { user, token, refreshToken } = action.payload;
            state.isAuthenticated = true;
            state.loggedInUser = user;
            state.authToken = token;
            state.refreshToken = refreshToken;
            state.error = null;
        },
        
        // Update token
        updateToken: (state, action) => {
            state.authToken = action.payload.token;
            if (action.payload.refreshToken) {
                state.refreshToken = action.payload.refreshToken;
            }
        },
        
        // Set session info
        setSessionInfo: (state, action) => {
            state.sessionInfo = action.payload;
        },
        
        // Set token validity
        setTokenValid: (state, action) => {
            state.tokenValid = action.payload;
        },
        
        // Increment login attempts
        incrementLoginAttempts: (state) => {
            state.loginAttempts += 1;
            state.lastLoginAttempt = new Date().toISOString();
        },
        
        // Reset login attempts
        resetLoginAttempts: (state) => {
            state.loginAttempts = 0;
            state.lastLoginAttempt = null;
        },
        
        // Set password change required
        setPasswordChangeRequired: (state, action) => {
            state.passwordChangeRequired = action.payload;
        },
        
        // Initialize from storage
        initializeFromStorage: (state) => {
            // This should be a pure function without side effects
            // The actual initialization logic should be handled in the component
            // This reducer just sets the initial state
            state.loading = false;
            state.error = null;
            // Don't call authService methods here as they can cause side effects
        },
        
        // OTP-specific reducers
        setOTPSession: (state, action) => {
            state.otpSession = { ...state.otpSession, ...action.payload };
        },
        
        clearOTPSession: (state) => {
            state.otpSession = {
                sessionId: null,
                credentialsVerified: false,
                otpSent: false,
                otpVerified: false,
                emailMasked: null,
                expiresIn: null,
                canResendIn: null,
                attemptsRemaining: null,
                maxAttempts: null
            };
            state.otpError = null;
        },
        
        setOTPLoading: (state, action) => {
            state.otpLoading = action.payload;
        },
        
        setOTPError: (state, action) => {
            state.otpError = action.payload;
        },
        
        updateOTPTimer: (state, action) => {
            const { expiresIn, canResendIn } = action.payload;
            if (expiresIn !== undefined) state.otpSession.expiresIn = expiresIn;
            if (canResendIn !== undefined) state.otpSession.canResendIn = canResendIn;
        },
        
        // Set authentication state from storage
        setAuthFromStorage: (state, action) => {
            const { user, token, refreshToken } = action.payload;
            if (user && token) {
                state.isAuthenticated = true;
                state.loggedInUser = user;
                state.authToken = token;
                state.refreshToken = refreshToken;
                state.error = null;
                // Update RBAC state from user data
                state.userRoles = user.roles || [];
                state.userPermissions = user.permissions || [];
                state.effectivePermissions = user.effective_permissions || {};
                state.hierarchyLevel = user.hierarchy_level || 0;
                state.canManageUsers = user.can_manage_users || false;
                state.allowedDepartments = user.allowed_departments || [];
            }
        },
        
        // Update RBAC state
        updateRBACState: (state, action) => {
            const { roles, permissions, effectivePermissions, hierarchyLevel, canManageUsers, allowedDepartments } = action.payload;
            state.userRoles = roles || state.userRoles;
            state.userPermissions = permissions || state.userPermissions;
            state.effectivePermissions = effectivePermissions || state.effectivePermissions;
            state.hierarchyLevel = hierarchyLevel !== undefined ? hierarchyLevel : state.hierarchyLevel;
            state.canManageUsers = canManageUsers !== undefined ? canManageUsers : state.canManageUsers;
            state.allowedDepartments = allowedDepartments || state.allowedDepartments;
        },
        
        // Clear RBAC state
        clearRBACState: (state) => {
            state.userRoles = [];
            state.userPermissions = [];
            state.effectivePermissions = {};
            state.hierarchyLevel = 0;
            state.canManageUsers = false;
            state.allowedDepartments = [];
        }
  },
  extraReducers: (builder) => {
    builder
            // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.loggedInUser = action.payload;
                state.isAuthenticated = true;
                state.authToken = action.payload.data?.access_token;
                state.refreshToken = action.payload.data?.refresh_token;
                state.error = null;
                state.loginAttempts = 0;
                state.lastLoginAttempt = null;
                // Update RBAC state from login response
                const userData = action.payload.data?.user_info || action.payload.data?.user || {};
                state.userRoles = userData.roles || [];
                state.userPermissions = userData.permissions || [];
                state.effectivePermissions = userData.effective_permissions || {};
                state.hierarchyLevel = userData.hierarchy_level || 0;
                state.canManageUsers = userData.can_manage_users || false;
                state.allowedDepartments = userData.allowed_departments || [];
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
                state.error = action.payload?.message || action.error.message;
                state.isAuthenticated = false;
                state.loggedInUser = null;
                state.authToken = null;
                state.refreshToken = null;
                state.loginAttempts += 1;
                state.lastLoginAttempt = new Date().toISOString();
            })
            
            // Logout
            .addCase(logout.pending, (state) => {
                state.loading = true;
            })
            .addCase(logout.fulfilled, (state) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.loggedInUser = null;
                state.authToken = null;
                state.refreshToken = null;
                state.sessionInfo = null;
                state.tokenValid = null;
                state.error = null;
                state.loginAttempts = 0;
                state.lastLoginAttempt = null;
                state.passwordChangeRequired = false;
            })
            .addCase(logout.rejected, (state, action) => {
                state.loading = false;
                // Even if logout fails, clear the state
                state.isAuthenticated = false;
                state.loggedInUser = null;
                state.authToken = null;
                state.refreshToken = null;
                state.sessionInfo = null;
                state.tokenValid = null;
                state.error = null;
            })
            
            // Refresh Token
            .addCase(refreshToken.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(refreshToken.fulfilled, (state, action) => {
                state.loading = false;
                state.authToken = action.payload.token;
                state.error = null;
            })
            .addCase(refreshToken.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
                // If refresh fails, user needs to login again
                state.isAuthenticated = false;
                state.loggedInUser = null;
                state.authToken = null;
                state.refreshToken = null;
            })
            
            // Validate Token
            .addCase(validateToken.pending, (state) => {
                state.loading = true;
            })
            .addCase(validateToken.fulfilled, (state, action) => {
                state.loading = false;
                state.tokenValid = action.payload.isValid;
                if (!action.payload.isValid) {
                    state.isAuthenticated = false;
                    state.loggedInUser = null;
                    state.authToken = null;
                    state.refreshToken = null;
                }
            })
            .addCase(validateToken.rejected, (state, action) => {
                state.loading = false;
                state.tokenValid = false;
                state.isAuthenticated = false;
                state.loggedInUser = null;
                state.authToken = null;
                state.refreshToken = null;
            })
            
            // Change Password
            .addCase(changePassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(changePassword.fulfilled, (state, action) => {
                state.loading = false;
                state.passwordChangeRequired = false;
                state.error = null;
            })
            .addCase(changePassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })
            
            // Forgot Password
            .addCase(forgotPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(forgotPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(forgotPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })
            
            // Reset Password
            .addCase(resetPassword.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(resetPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
            })
            .addCase(resetPassword.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })
            
            // Get Session Info
            .addCase(getSessionInfo.pending, (state) => {
                state.loading = true;
            })
            .addCase(getSessionInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.sessionInfo = action.payload.data;
            })
            .addCase(getSessionInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })
            
            // Legacy SMS Verification
      .addCase(smsVerification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(smsVerification.fulfilled, (state, action) => {
        state.loading = false;
        state.smsVerificationData = action.payload;
      })
      .addCase(smsVerification.rejected, (state, action) => {
        state.loading = false;
                state.error = action.payload?.message || action.error.message;
      })
            
            // Legacy Get Staff Details
      .addCase(getStaffDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStaffDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.getStaffDetailsData = action.payload;
      })
      .addCase(getStaffDetails.rejected, (state, action) => {
        state.loading = false;
                state.error = action.payload?.message || action.error.message;
            })
            
            // OTP Authentication - Initiate Login
            .addCase(initiateOTPLogin.pending, (state) => {
                state.otpLoading = true;
                state.otpError = null;
                state.error = null;
            })
            .addCase(initiateOTPLogin.fulfilled, (state, action) => {
                state.otpLoading = false;
                state.otpError = null;
                // Update OTP session with response data
                state.otpSession = {
                    sessionId: action.payload.data?.session_id || action.payload.session_id,
                    credentialsVerified: true,
                    otpSent: action.payload.data?.otp_sent || action.payload.otp_sent,
                    otpVerified: false,
                    emailMasked: action.payload.data?.email_masked || action.payload.email_masked,
                    expiresIn: action.payload.data?.expires_in || action.payload.expires_in,
                    canResendIn: action.payload.data?.can_resend_in || action.payload.can_resend_in,
                    attemptsRemaining: action.payload.data?.max_attempts || action.payload.max_attempts,
                    maxAttempts: action.payload.data?.max_attempts || action.payload.max_attempts
                };
            })
            .addCase(initiateOTPLogin.rejected, (state, action) => {
                state.otpLoading = false;
                state.otpError = action.payload?.message || action.error.message;
                state.error = action.payload?.message || action.error.message;
                // Increment login attempts on failure
                state.loginAttempts += 1;
                state.lastLoginAttempt = new Date().toISOString();
            })
            
            // OTP Authentication - Verify OTP
            .addCase(verifyOTP.pending, (state) => {
                state.otpLoading = true;
                state.otpError = null;
            })
            .addCase(verifyOTP.fulfilled, (state, action) => {
                state.otpLoading = false;
                state.otpError = null;
                
                if (action.payload.success) {
                    // Mark OTP as verified
                    state.otpSession.otpVerified = true;
                    
                    // Set authentication state (similar to regular login)
                    const userData = action.payload.data || action.payload.user_info;
                    state.isAuthenticated = true;
                    state.loggedInUser = { data: { user_info: userData } };
                    state.authToken = action.payload.access_token;
                    state.refreshToken = action.payload.refresh_token;
                    state.sessionInfo = action.payload.session_info;
                    state.error = null;
                    state.loginAttempts = 0;
                    state.lastLoginAttempt = null;
                    
                    // Clear OTP session after successful login
                    state.otpSession = {
                        sessionId: null,
                        credentialsVerified: false,
                        otpSent: false,
                        otpVerified: false,
                        emailMasked: null,
                        expiresIn: null,
                        canResendIn: null,
                        attemptsRemaining: null,
                        maxAttempts: null
                    };
                } else {
                    // Update remaining attempts
                    if (action.payload.remaining_attempts !== undefined) {
                        state.otpSession.attemptsRemaining = action.payload.remaining_attempts;
                    }
                }
            })
            .addCase(verifyOTP.rejected, (state, action) => {
                state.otpLoading = false;
                state.otpError = action.payload?.message || action.error.message;
                
                // Update remaining attempts if provided
                if (action.payload?.remaining_attempts !== undefined) {
                    state.otpSession.attemptsRemaining = action.payload.remaining_attempts;
                }
            })
            
            // OTP Authentication - Resend OTP
            .addCase(resendOTP.pending, (state) => {
                state.otpLoading = true;
                state.otpError = null;
            })
            .addCase(resendOTP.fulfilled, (state, action) => {
                state.otpLoading = false;
                state.otpError = null;
                // Update timing information
                state.otpSession.expiresIn = action.payload.data?.expires_in || action.payload.expires_in;
                state.otpSession.canResendIn = action.payload.data?.can_resend_in || action.payload.can_resend_in;
                state.otpSession.attemptsRemaining = action.payload.data?.attempts_remaining || action.payload.attempts_remaining;
            })
            .addCase(resendOTP.rejected, (state, action) => {
                state.otpLoading = false;
                state.otpError = action.payload?.message || action.error.message;
            })
            
            // OTP Authentication - Get Status
            .addCase(getOTPStatus.pending, (state) => {
                // Don't set loading for status checks to avoid UI flickering
            })
            .addCase(getOTPStatus.fulfilled, (state, action) => {
                const statusData = action.payload.data || action.payload;
                state.otpSession = {
                    ...state.otpSession,
                    credentialsVerified: statusData.credentials_verified,
                    otpVerified: statusData.otp_verified,
                    expiresIn: statusData.expires_in,
                    canResendIn: statusData.can_resend_in,
                    attemptsRemaining: statusData.attempts_remaining,
                    emailMasked: statusData.email_masked
                };
            })
            .addCase(getOTPStatus.rejected, (state, action) => {
                // Don't set error for status check failures
                // Just clear the session if it's invalid
                if (action.payload?.message?.includes('Invalid') || action.payload?.message?.includes('expired')) {
                    state.otpSession = {
                        sessionId: null,
                        credentialsVerified: false,
                        otpSent: false,
                        otpVerified: false,
                        emailMasked: null,
                        expiresIn: null,
                        canResendIn: null,
                        attemptsRemaining: null,
                        maxAttempts: null
                    };
                }
            });
  },
});

// Export actions
export const { 
    changeValue, 
    clearAuth, 
    setAuth, 
    updateToken, 
    setSessionInfo, 
    setTokenValid, 
    incrementLoginAttempts, 
    resetLoginAttempts, 
    setPasswordChangeRequired,
    initializeFromStorage,
    setAuthFromStorage,
    updateRBACState,
    clearRBACState,
    setOTPSession,
    clearOTPSession,
    setOTPLoading,
    setOTPError,
    updateOTPTimer
} = userSlice.actions;

// Export selectors
export const selectUser = (state) => state.user;
export const selectIsAuthenticated = (state) => state.user.isAuthenticated;
export const selectLoggedInUser = (state) => state.user.loggedInUser;
export const selectAuthToken = (state) => state.user.authToken;
export const selectLoading = (state) => state.user.loading;
export const selectError = (state) => state.user.error;
export const selectSessionInfo = (state) => state.user.sessionInfo;
export const selectTokenValid = (state) => state.user.tokenValid;
export const selectLoginAttempts = (state) => state.user.loginAttempts;
export const selectPasswordChangeRequired = (state) => state.user.passwordChangeRequired;

// RBAC Selectors
export const selectUserRoles = (state) => state.user.userRoles;
export const selectUserPermissions = (state) => state.user.userPermissions;
export const selectEffectivePermissions = (state) => state.user.effectivePermissions;
export const selectHierarchyLevel = (state) => state.user.hierarchyLevel;
export const selectCanManageUsers = (state) => state.user.canManageUsers;
export const selectAllowedDepartments = (state) => state.user.allowedDepartments;

// Computed RBAC Selectors
export const selectHasPermission = (permission) => (state) => {
    const permissions = state.user.effectivePermissions;
    return permissions[permission] === true;
};

export const selectHasRole = (role) => (state) => {
    const roles = state.user.userRoles;
    return roles.includes(role);
};

export const selectCanAccessDepartment = (department) => (state) => {
    const allowedDepartments = state.user.allowedDepartments;
    return allowedDepartments.length === 0 || allowedDepartments.includes(department);
};

// OTP Selectors
export const selectOTPSession = (state) => state.user.otpSession;
export const selectOTPLoading = (state) => state.user.otpLoading;
export const selectOTPError = (state) => state.user.otpError;
export const selectOTPSessionId = (state) => state.user.otpSession.sessionId;
export const selectOTPCredentialsVerified = (state) => state.user.otpSession.credentialsVerified;
export const selectOTPSent = (state) => state.user.otpSession.otpSent;
export const selectOTPVerified = (state) => state.user.otpSession.otpVerified;
export const selectOTPEmailMasked = (state) => state.user.otpSession.emailMasked;
export const selectOTPExpiresIn = (state) => state.user.otpSession.expiresIn;
export const selectOTPCanResendIn = (state) => state.user.otpSession.canResendIn;
export const selectOTPAttemptsRemaining = (state) => state.user.otpSession.attemptsRemaining;
export const selectOTPMaxAttempts = (state) => state.user.otpSession.maxAttempts;

// OTP Computed Selectors
export const selectIsOTPLoginInProgress = (state) => {
    const session = state.user.otpSession;
    return session.credentialsVerified && session.otpSent && !session.otpVerified;
};

export const selectCanResendOTP = (state) => {
    const session = state.user.otpSession;
    return session.credentialsVerified && session.otpSent && 
           (session.canResendIn === null || session.canResendIn <= 0);
};

export const selectIsOTPExpired = (state) => {
    const session = state.user.otpSession;
    return session.expiresIn !== null && session.expiresIn <= 0;
};

export const selectOTPEnabled = (state) => state.user.features.otpEnabled;

// Export reducer
export default userSlice.reducer; 